import { useRef, useState } from "preact/hooks"
import { useFetch } from "../hooks/useFetch"
import type { KidProfile, Me, ProfileStats, TopicBadge } from "../types"
import { LoadError } from "./LoadError"
import {
	changePassword,
	createProfile,
	deleteProfile,
	fetchMyStats,
	fetchProfiles,
	fetchPublicProfile,
	fetchRecentSoloPlays,
	renameProfile,
	switchProfile,
	updateProfileSettings,
} from "./lib/api"
import { Modal } from "./Modal"
import { PlanSection } from "./Plan"
import { contactEmail, SiteFooter } from "./SiteFooter"
import { TopicIcon } from "./TopicIcons"

interface ProfileProps {
	me: Me
	// Called after this session has been switched into a kid profile — App
	// re-reads the session and swaps the shell for kid mode.
	onProfileChanged: () => void
	// null renders the signed-in user's own profile (/me); a username
	// renders that user's public profile (/u/:username), stats only shown
	// when they've opted in.
	username: string | null
}

function Stat({ label, value }: { label: string; value: string | number }) {
	// Equal-width boxes that wrap with the container: flex-basis 8.5rem
	// gives 2-across on phones, 4-across on a laptop — the same as the old
	// Bootstrap grid, but computed by the browser, no breakpoints.
	return (
		<div class="border rounded p-2 text-center" style={{ flex: "1 1 8.5rem" }}>
			<div class="fw-semibold">{value}</div>
			<div class="muted small">{label}</div>
		</div>
	)
}

// The badge rules, restated for the empty state so a player knows what to
// aim at. Mirrors badgeMinAnswered and the last badgeTiers threshold in
// internal/server/profile.go — the server awards the badges, this is only
// the sentence that explains them, so keep the two in step.
const badgeMinAnswered = 20
const badgeMinAccuracy = 60

// The badge wall — the topics this player is good at. The tier is named in
// text beside the accuracy, not just painted on: the medal colour is the
// decoration, never the only way to read the badge.
function TopicBadges({ badges }: { badges: TopicBadge[] }) {
	return (
		<div class="flex wrap gap-2">
			{badges.map((b) => (
				<span
					key={b.topic}
					class={`medal medal-${b.tier}`}
					title={`${b.correct} of ${b.answered} questions answered correctly`}
				>
					<span class="medal-disc">
						<TopicIcon topic={b.topic} size="1.4rem" />
					</span>
					<span>
						<span class="block fw-semibold small">{b.name}</span>
						<span class="block small capitalize">
							{b.tier} · {b.accuracy}%
						</span>
					</span>
				</span>
			))}
		</div>
	)
}

function StatsCards({ stats }: { stats: ProfileStats }) {
	return (
		<div class="flex wrap gap-3">
			<Stat label="Solo plays" value={stats.solo.plays} />
			<Stat label="Best solo score" value={`${stats.solo.best_score}/10`} />
			<Stat label="Matches" value={stats.matches.played} />
			<Stat
				label="Won / Lost / Draw"
				value={`${stats.matches.won} / ${stats.matches.lost} / ${stats.matches.drawn}`}
			/>
		</div>
	)
}

// The two pages are two components rather than one with an `isOwn` branch,
// so that each one's fetches run only on its own page. (App keys the public
// one by username, so a change of username is a fresh mount and a fresh fetch.)
export function Profile({ me, username, onProfileChanged }: ProfileProps) {
	return username === null ? (
		<OwnProfile me={me} onProfileChanged={onProfileChanged} />
	) : (
		<OtherProfile username={username} />
	)
}

// /u/:username — someone else's profile: their scores if they have opted in,
// otherwise just the fact that it is private.
function OtherProfile({ username }: { username: string }) {
	const { data: profile, error: profileError } = useFetch(
		() => fetchPublicProfile(username),
		"Failed to load profile",
	)

	return (
		<div class="overflow-auto h-100">
			<div class="container page">
				<h1>{username}</h1>
				{profileError && <div class="alert danger inline">{profileError}</div>}
				{profile &&
					(profile.user.profile_public && profile.stats ? (
						<>
							{profile.stats.badges.length > 0 && (
								<div class="stack gap-2">
									<h2>Best topics</h2>
									<TopicBadges badges={profile.stats.badges} />
								</div>
							)}
							<StatsCards stats={profile.stats} />
							<p class="muted small">
								This profile is public — anyone who knows the username can see
								these scores.
							</p>
						</>
					) : (
						<p class="muted">This profile is private.</p>
					))}
			</div>

			<SiteFooter />
		</div>
	)
}

// /me — the signed-in user's own page.
function OwnProfile({ me, onProfileChanged }: Omit<ProfileProps, "username">) {
	// --- visibility setting --------------------------------------------------
	const [profilePublic, setProfilePublic] = useState(me.profile_public)
	const [settingsError, setSettingsError] = useState("")
	const [saving, setSaving] = useState(false)

	// --- own scores ----------------------------------------------------------
	// One fetch, shared by "Best topics" and "My scores" below: the loading
	// spinner and a failed fetch are shown exactly once, not once per section,
	// so the two headings only ever branch on the data once it has arrived.
	const {
		data: stats,
		error: statsError,
		reload: loadStats,
	} = useFetch(fetchMyStats, "Couldn't load your scores.")
	const {
		data: recent,
		error: recentError,
		reload: loadRecent,
	} = useFetch(fetchRecentSoloPlays, "Couldn't load your recent games.")

	// --- password form --------------------------------------------------------
	const pwDetailsRef = useRef<HTMLDetailsElement>(null)
	const currentPwRef = useRef<HTMLInputElement>(null)
	const [currentPassword, setCurrentPassword] = useState("")
	const [newPassword, setNewPassword] = useState("")
	const [confirmPassword, setConfirmPassword] = useState("")
	const [pwError, setPwError] = useState("")
	const [pwSuccess, setPwSuccess] = useState("")
	const [changing, setChanging] = useState(false)

	// --- kid profiles ---------------------------------------------------------
	// The list itself has the usual three states; `profilesAction` is separate
	// because it belongs to a *button* (a duplicate name, a failed delete), not
	// to the fetch, and must not be mistaken for the list having failed.
	const {
		data: profiles,
		error: profilesError,
		reload: loadProfiles,
		setData: setProfiles,
	} = useFetch(fetchProfiles, "Couldn't load your profiles.")
	const [profilesAction, setProfilesAction] = useState("")
	const [profileBusy, setProfileBusy] = useState(false)
	const [newProfileName, setNewProfileName] = useState("")
	// Which row is being renamed, and the text in its input.
	const [renamingId, setRenamingId] = useState<number | null>(null)
	const [renameName, setRenameName] = useState("")
	// The profile the delete confirmation is open for. Deleting takes its
	// rounds and badges with it, so the button only ever opens this dialog.
	const [deleting, setDeleting] = useState<KidProfile | null>(null)

	async function handleVisibility(e: Event) {
		const next = (e.target as HTMLInputElement).value === "public"
		setSaving(true)
		setSettingsError("")
		try {
			await updateProfileSettings(next)
			setProfilePublic(next)
		} catch (e) {
			setSettingsError(
				e instanceof Error ? e.message : "Failed to update settings",
			)
		} finally {
			setSaving(false)
		}
	}

	// Every one of these updates `profiles` in place once the request has
	// resolved, rather than refetching the list: the row the user just acted on
	// is the one they are looking at, and a round trip to re-read it would only
	// make it flicker.
	async function handleAddProfile(e: Event) {
		e.preventDefault()
		const name = newProfileName.trim()
		if (!name) return
		setProfileBusy(true)
		setProfilesAction("")
		try {
			const created = await createProfile(name)
			setProfiles((prev) => (prev ? [...prev, created] : [created]))
			setNewProfileName("")
		} catch (e) {
			// 409 for a duplicate name or a full account, 400 for a bad one —
			// each arrives as a sentence meant for the person reading it.
			setProfilesAction(
				e instanceof Error ? e.message : "Couldn't add that profile.",
			)
		} finally {
			setProfileBusy(false)
		}
	}

	async function handleRename(e: Event) {
		e.preventDefault()
		const id = renamingId
		const name = renameName.trim()
		if (id === null || !name) return
		setProfileBusy(true)
		setProfilesAction("")
		try {
			await renameProfile(id, name)
			setProfiles((prev) =>
				prev ? prev.map((p) => (p.id === id ? { ...p, name } : p)) : prev,
			)
			setRenamingId(null)
		} catch (e) {
			setProfilesAction(
				e instanceof Error ? e.message : "Couldn't rename that profile.",
			)
		} finally {
			setProfileBusy(false)
		}
	}

	async function handleDeleteProfile() {
		const target = deleting
		if (!target) return
		setProfileBusy(true)
		setProfilesAction("")
		try {
			await deleteProfile(target.id)
			setProfiles((prev) =>
				prev ? prev.filter((p) => p.id !== target.id) : prev,
			)
			setDeleting(null)
		} catch (e) {
			setProfilesAction(
				e instanceof Error ? e.message : "Couldn't delete that profile.",
			)
			setDeleting(null)
		} finally {
			setProfileBusy(false)
		}
	}

	// Switching replaces the whole app, so there is nothing to update here —
	// App re-reads the session and renders kid mode from "/".
	async function handleSwitchProfile(p: KidProfile) {
		setProfileBusy(true)
		setProfilesAction("")
		try {
			await switchProfile(p.id)
			onProfileChanged()
		} catch (e) {
			setProfilesAction(
				e instanceof Error ? e.message : `Couldn't switch to ${p.name}.`,
			)
			setProfileBusy(false)
		}
	}

	// Focus the first field when the collapsible is expanded, so the user can
	// start typing immediately (and tab order stays sane).
	function handlePwToggle() {
		if (pwDetailsRef.current?.open) currentPwRef.current?.focus()
	}

	async function handlePassword(e: Event) {
		e.preventDefault()
		setPwError("")
		setPwSuccess("")
		if (newPassword !== confirmPassword) {
			setPwError("New passwords do not match")
			return
		}
		setChanging(true)
		try {
			await changePassword(currentPassword, newPassword)
			setCurrentPassword("")
			setNewPassword("")
			setConfirmPassword("")
			setPwSuccess("Password updated.")
		} catch (e) {
			setPwError(e instanceof Error ? e.message : "Failed to change password")
		} finally {
			setChanging(false)
		}
	}

	return (
		<div class="overflow-auto h-100">
			<div class="container page">
				<div class="flex baseline gap-2">
					<h1>{me.username}</h1>
					<span class="muted small">{me.email}</span>
				</div>

				{/* The plan comes first: it is the one section with something to
				    do (upgrade, fix a card, see when Pro ends). It is also where the
				    Dodo checkout returns to (/me?checkout=done); the section reads
				    the flag once, on mount, and waits for the webhook. */}
				<PlanSection
					checkoutDone={
						new URLSearchParams(window.location.search).get("checkout") ===
						"done"
					}
				/>

				{/* Profiles sit between the plan and the scores: they are the one
				    other thing on this page that is about *managing* the account,
				    and every round they play is charged to the plan above. */}
				<div class="stack gap-2">
					<h2>Kids profiles</h2>
					<p class="muted small">
						A profile is for one of your kids: it plays the kids' topics only,
						keeps its own scores and badges, and its rounds count against your
						plan.
					</p>
					{profilesAction && (
						<div class="alert danger inline">{profilesAction}</div>
					)}
					{profilesError ? (
						<LoadError message={profilesError} onRetry={loadProfiles} />
					) : !profiles ? (
						<div class="spinner sm" role="status" />
					) : (
						<div class="stack gap-3" style="max-width: 34rem;">
							{profiles.length === 0 ? (
								<p class="muted small">
									No profiles yet. Add one and hand them the phone from your
									home page.
								</p>
							) : (
								<div class="list">
									{profiles.map((p) => (
										<div class="stack gap-2" key={p.id}>
											{renamingId === p.id ? (
												<form class="flex wrap gap-2" onSubmit={handleRename}>
													<div class="grow">
														<input
															style={{ minWidth: "10rem" }}
															// Mirrors validateProfileName in profiles.go.
															maxLength={30}
															value={renameName}
															onInput={(e) =>
																setRenameName(
																	(e.target as HTMLInputElement).value,
																)
															}
														/>
													</div>
													<button
														type="submit"
														class="primary sm"
														disabled={profileBusy}
													>
														Save
													</button>
													<button
														type="button"
														class="sm"
														disabled={profileBusy}
														onClick={() => setRenamingId(null)}
													>
														Cancel
													</button>
												</form>
											) : (
												<div class="flex between center gap-2 wrap">
													<div class="flex center gap-2 wrap">
														<span class="fw-semibold">{p.name}</span>
														<span class="muted small">
															{p.stats.solo.plays}{" "}
															{p.stats.solo.plays === 1 ? "round" : "rounds"}
														</span>
													</div>
													<div class="flex gap-2">
														<button
															type="button"
															class="outline sm"
															disabled={profileBusy}
															onClick={() => handleSwitchProfile(p)}
														>
															Switch
														</button>
														<button
															type="button"
															class="sm"
															disabled={profileBusy}
															onClick={() => {
																setRenamingId(p.id)
																setRenameName(p.name)
															}}
														>
															Rename
														</button>
														<button
															type="button"
															class="link danger sm"
															disabled={profileBusy}
															onClick={() => setDeleting(p)}
														>
															Delete
														</button>
													</div>
												</div>
											)}
											{p.stats.badges.length > 0 && (
												<TopicBadges badges={p.stats.badges} />
											)}
										</div>
									))}
								</div>
							)}
							<form class="flex gap-2" onSubmit={handleAddProfile}>
								<div class="grow">
									<input
										placeholder="Name"
										aria-label="New profile name"
										maxLength={30}
										value={newProfileName}
										onInput={(e) =>
											setNewProfileName((e.target as HTMLInputElement).value)
										}
									/>
								</div>
								<button type="submit" class="primary" disabled={profileBusy}>
									Add a profile
								</button>
							</form>
						</div>
					)}

					<Modal
						open={deleting !== null}
						onClose={() => setDeleting(null)}
						title="Delete this profile?"
					>
						{deleting && (
							<div class="stack gap-3">
								<p>
									This deletes {deleting.name}'s rounds and badges. It cannot be
									undone.
								</p>
								<div class="flex wrap jc-end gap-2">
									<button
										type="button"
										disabled={profileBusy}
										onClick={() => setDeleting(null)}
									>
										Keep it
									</button>
									<button
										type="button"
										class="danger"
										disabled={profileBusy}
										onClick={handleDeleteProfile}
									>
										Delete {deleting.name}
									</button>
								</div>
							</div>
						)}
					</Modal>
				</div>

				{/* Best topics and My scores share one fetch (fetchMyStats), so its
				    loading and failed states are shown once here, not once per
				    section — a section only ever branches on `stats` once it has
				    actually loaded. */}
				{statsError ? (
					<LoadError message={statsError} onRetry={loadStats} />
				) : !stats ? (
					<div class="spinner sm" role="status" />
				) : (
					<>
						<div class="stack gap-2">
							<h2>Best topics</h2>
							{/* "No badges yet" is only true once the stats actually
							    arrived, so it only ever renders from loaded data. */}
							{stats.badges.length === 0 ? (
								<p class="muted small">
									Answer {badgeMinAnswered} questions in one topic and get at
									least {badgeMinAccuracy}% of them right to earn a badge for
									it.
								</p>
							) : (
								<TopicBadges badges={stats.badges} />
							)}
						</div>

						<div class="stack gap-2">
							<h2>My scores</h2>
							<StatsCards stats={stats} />
						</div>
					</>
				)}

				<div class="stack gap-2">
					<h2>Recent games</h2>
					{/* "No games played yet" is only true once the list actually
					    arrived; a failed fetch gets the error, not the empty copy. */}
					{recentError ? (
						<LoadError message={recentError} onRetry={loadRecent} />
					) : !recent ? (
						<div class="spinner sm" role="status" />
					) : recent.length === 0 ? (
						<p class="muted small">No games played yet.</p>
					) : (
						<div class="list" style="max-width: 28rem;">
							{recent.map((s) => (
								<a
									href={`/solo/${s.id}`}
									class="flex between center"
									key={s.id}
								>
									<span>Solo {s.topic_name}</span>
									<span class="muted small">{s.score}/10</span>
								</a>
							))}
						</div>
					)}
				</div>

				<div class="stack gap-2">
					<h2>Profile visibility</h2>
					{settingsError && (
						<div class="alert danger inline">{settingsError}</div>
					)}
					<fieldset class="stack gap-2" disabled={saving}>
						<div class="stack gap-1">
							<label class="check">
								<input
									type="radio"
									name="profileVisibility"
									value="public"
									checked={profilePublic}
									onChange={handleVisibility}
								/>
								Public
							</label>
							<p class="hint">
								Any logged-in user who knows your username can view your scores
								at{" "}
								<a href={`/u/${me.username}`} class="plain">
									/u/{me.username}
								</a>
								.
							</p>
						</div>
						<div class="stack gap-1">
							<label class="check">
								<input
									type="radio"
									name="profileVisibility"
									value="private"
									checked={!profilePublic}
									onChange={handleVisibility}
								/>
								Private
							</label>
							<p class="hint">
								Only you can view your scores; other players see a private
								profile.
							</p>
						</div>
					</fieldset>
				</div>

				<div>
					<details ref={pwDetailsRef} onToggle={handlePwToggle}>
						<summary>
							<h2 style={{ display: "inline" }}>Change password</h2>
						</summary>
						<form
							onSubmit={handlePassword}
							class="stack gap-2 mt-3"
							style="max-width: 28rem;"
						>
							<input
								ref={currentPwRef}
								type="password"
								placeholder="Current password"
								autocomplete="current-password"
								value={currentPassword}
								onInput={(e) =>
									setCurrentPassword((e.target as HTMLInputElement).value)
								}
							/>
							<input
								type="password"
								placeholder="New password (at least 6 characters)"
								autocomplete="new-password"
								// Mirrors minPasswordLength in auth.go.
								minLength={6}
								value={newPassword}
								onInput={(e) =>
									setNewPassword((e.target as HTMLInputElement).value)
								}
							/>
							<input
								type="password"
								placeholder="Confirm new password"
								autocomplete="new-password"
								value={confirmPassword}
								onInput={(e) =>
									setConfirmPassword((e.target as HTMLInputElement).value)
								}
							/>
							<button type="submit" class="primary" disabled={changing}>
								Change password
							</button>
							{pwError && <p class="text-danger small">{pwError}</p>}
							{pwSuccess && <p class="text-success small">{pwSuccess}</p>}
						</form>
					</details>
				</div>

				<div class="stack gap-2">
					<h2>Contact</h2>
					<p class="muted small">
						Found a question with a wrong answer, or have a topic you'd like to
						see? Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
					</p>
				</div>

				<SiteFooter />
			</div>
		</div>
	)
}
